import * as React from "react"
import { X, ChevronDown } from "lucide-react"
import { Badge } from "./badge"
import { Command, CommandGroup, CommandItem, CommandInput } from "./command"
import { Command as CommandPrimitive } from "cmdk"
import { cn } from "../../lib/utils"

export type Option = {
  value: string
  label: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  className?: string
  placeholder?: string
}

export function MultiSelect({
  options,
  selected,
  onChange,
  className,
  placeholder = "Select options...",
  ...props
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleUnselect = (option: string) => {
    onChange(selected.filter((s) => s !== option))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = e.target as HTMLInputElement
    if (input.value === "") {
      switch (e.key) {
        case "Delete":
        case "Backspace":
          if (selected.length > 0) {
            handleUnselect(selected[selected.length - 1])
          }
          break
        case "Escape":
          setOpen(false)
          break
        default:
          break
      }
    }
  }

  const handleSelect = (value: string) => {
    const isSelected = selected.includes(value)
    if (isSelected) {
      onChange(selected.filter((s) => s !== value))
    } else {
      onChange([...selected, value])
    }
    setInputValue("")
  }

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(inputValue.toLowerCase())
  )

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={cn(
          "relative w-full cursor-pointer rounded-md border border-input bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring",
          open && "ring-2 ring-ring",
          className
        )}
        onClick={() => setOpen(!open)}
      >
        <div className="flex min-h-[38px] w-full flex-wrap items-center gap-1 p-1">
          {selected.length > 0 ? (
            selected.map((option) => {
              const selectedOption = options.find((o) => o.value === option)
              return (
                <Badge
                  key={option}
                  variant="secondary"
                  className="rounded-sm px-1 font-normal bg-gray-100 text-gray-900"
                >
                  {selectedOption?.label}
                  <button
                    className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleUnselect(option)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUnselect(option)
                    }}
                    aria-label={`Remove ${selectedOption?.label}`}
                  >
                    <X className="h-3 w-3 text-gray-500 hover:text-gray-900" />
                  </button>
                </Badge>
              )
            })
          ) : (
            <span className="px-2 py-1 text-gray-500">{placeholder}</span>
          )}
          <ChevronDown className={cn(
            "ml-auto h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200",
            open && "rotate-180"
          )} />
        </div>
      </div>
      {open && (
        <div 
          className="absolute z-50 mt-1 w-full rounded-md border bg-white text-gray-900 shadow-md outline-none animate-in"
          onClick={(e) => e.stopPropagation()}
        >
          <Command className="w-full border-none bg-transparent">
            <CommandInput
              placeholder="Search..."
              value={inputValue}
              onValueChange={setInputValue}
              onKeyDown={handleKeyDown}
              className="border-none focus:ring-0 text-gray-900"
            />
            <CommandGroup className="max-h-[200px] overflow-auto">
              {filteredOptions.map((option) => {
                const isSelected = selected.includes(option.value)
                return (
                  <div
                    key={option.value}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 px-2 py-1.5",
                      "hover:bg-gray-100",
                      "text-gray-900"
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (isSelected) {
                        onChange(selected.filter((s) => s !== option.value))
                      } else {
                        onChange([...selected, option.value])
                      }
                      setInputValue("")
                    }}
                  >
                    <div
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded-sm border border-gray-300",
                        isSelected
                          ? "bg-[#e04530] border-[#e04530] text-white"
                          : "bg-white"
                      )}
                    >
                      {isSelected && (
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm">{option.label}</span>
                  </div>
                )
              })}
              {filteredOptions.length === 0 && (
                <div className="py-6 text-center text-sm text-gray-500">
                  No options found.
                </div>
              )}
            </CommandGroup>
          </Command>
        </div>
      )}
    </div>
  )
} 